import { cx } from "@emotion/css";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import { Menu } from "src/components/Default/LekUI/Menu";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { useDeleteFolder } from "src/hooks/useFolders";
import { useOverflowItems } from "src/hooks/useOverflowItems";
import { FolderColorIcon } from "./FolderColorIcon";
import type { Folder } from "./types";

interface FolderListItemProps {
	folder: Folder;
}

export const FolderListItem = ({ folder }: FolderListItemProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const { openModal } = useModals();
	const { mutateAsync: deleteFolder } = useDeleteFolder();

	const folderColor = folder.details?.color;

	const hasExams = Number(folder?.examCount) > 0;
	const hasCourses = Number(folder?.courseCount) > 0;
	const hasFlashcards = Number(folder?.decksCount) > 0;
	const hasNotes = Number(folder?.notesCount) > 0;

	const allTags = useMemo(
		() =>
			[
				hasFlashcards && t("folder.tabs.flashcards", "Fiszki"),
				hasExams && t("folder.tabs.exams", "Egzaminy"),
				hasNotes && t("folder.tabs.notes", "Notatki"),
				hasCourses && t("folder.tabs.courses", "Kursy"),
			].filter(Boolean) as string[],
		[hasFlashcards, hasExams, hasNotes, hasCourses, t],
	);

	const {
		containerRef,
		displayedItems: displayedTags,
		hiddenCount,
	} = useOverflowItems(allTags);

	const menuItems = [
		{
			title: t("folder.editFolder", "Edytuj folder"),
			onClick: () => {
				openModal(ModalsNames.EditFolderModal, {
					folderId: folder.id,
					folderName: folder.name,
					folderColor: folder.details?.color,
				});
			},
			Icon: <Pencil className="icon-small" />,
			testId: "folder-list-edit-item",
		},
		{
			title: t("folder.deleteFolder", "Usuń folder"),
			onClick: () => {
				openModal(ModalsNames.DeleteItemModal, {
					title: t("modals.deleteItem.folder.title", "Usunąć folder?"),
					description: t(
						"modals.deleteItem.folder.description",
						"Usunięty zostanie tylko folder — wszystkie egzaminy, kursy, zestawy fiszek i notatki, które się w nim znajdują, pozostaną na Twoim koncie.",
					),
					confirmText: t("modals.deleteItem.folder.confirm", "Usuń folder"),
					itemLabel: folder.name,
					onDelete: () => deleteFolder(folder.id),
				});
			},
			Icon: <Trash2 className="icon-small icon-error" />,
			itemTextClassName: "text-error",
			testId: "folder-list-delete-item",
		},
	];

	return (
		<div
			data-test-id="folder-item"
			data-folder-id={folder.id}
			onClick={() =>
				history.push(generatePath(routing.paths.folder, { id: folder.id }))
			}
			className={cx(
				"flex items-center w-100 p-16 border-radius-16 folder-list-item",
			)}
		>
			<FolderColorIcon color={folderColor} />
			<div className="flex items-center flex-1 gap-16 min-w-0">
				<h3 className="m-0 size-20 color-black-100 text-ellipsis-1 min-w-0 flex-shrink">
					{folder.name}
				</h3>
				<div
					ref={containerRef}
					className="flex items-center gap-8 flex-nowrap of-hidden min-w-0 flex-1"
				>
					{displayedTags.map((label, i) => (
						<div
							key={i}
							data-tag
							className="folder-item-tag size-14 ws-nowrap flex-shrink-0"
						>
							{label}
						</div>
					))}
				</div>
				{hiddenCount > 0 && (
					<div className="folder-item-tag size-14 ws-nowrap flex-shrink-0">
						+{hiddenCount}
					</div>
				)}
			</div>
			<div onClick={(e) => e.stopPropagation()}>
				<Menu
					icon={<MoreVertical className="icon-small" />}
					buttonClassName="folder-list-menu-button"
					buttonTestId="folder-list-menu-button"
					anchorId={`folder-list-actions-${folder.id}`}
					closeAfterClick
					items={menuItems}
				/>
			</div>
		</div>
	);
};
